import { Fragment } from 'react';
import { groupBy, pick } from 'lodash-es';
import moment from 'moment';
import { colors } from '@karnott/colors';
import { TaskDoneIcon, TaskDraftIcon, TaskInProgressIcon, TaskTodoIcon } from '@karnott/icons';
import { convertAreaToSquaredMeters, sortAlphaBeta } from '.';
import { USER_CONSTANTS } from '../constants';
import { PHYTO_VIRTUAL_ARTICLE_ID } from '../constants/articleConstants';
import { COLOR_PARCELS_BY, COLOR_TRACKS_BY } from '../constants/colorConstants';
import { EQUIPMENT_CONSTANTS } from '../constants/equipmentConstants';
import {
  PHYTO_TASK_TYPE,
  TASK_METADATA_QUESTION_FORMAT,
  TASK_METADATA_QUESTION_TYPE,
  TASK_STATUS,
  VISIBLE_TASK_METADATA_GROUPS,
} from '../constants/taskConstants';
import { I18n } from '../i18n';
import { getArticleRangeValue, getVirtualPhytoArticle, sortTaskArticles } from './articles';
import { getMetadataResponseLabel, isPhytoTaskType } from './taskTypes';
import { getUserName } from './user';
import { isNumber } from './validation';
import { filterWorksiteParcelsForTask, getEquipmentFromWorksite, getWorksiteKey } from './worksites';

export function getTaskStatusIcon(status) {
  switch (status) {
    case TASK_STATUS.TO_DO:
      return TaskTodoIcon;
    case TASK_STATUS.IN_PROGRESS:
      return TaskInProgressIcon;
    case TASK_STATUS.DRAFT:
      return TaskDraftIcon;
    default:
      return TaskDoneIcon;
  }
}

/**
 * @template T
 * @param {T[]} array
 * @param {(el: T, i: number) => import('react').ReactNode} mapFunction
 * @param {(el: T) => import('react').Key} getKey
 *
 * @returns {import('react').ReactNode}
 */
export function mapAndJoin(array, mapFunction, getKey) {
  return array.map((el, i) => (
    <Fragment key={getKey(el)}>
      {mapFunction(el, i)}
      {i === array.length - 1 ? '' : ', '}
    </Fragment>
  ));
}

export function getTaskWorksiteTypes(taskType) {
  return taskType === PHYTO_TASK_TYPE.SEMENCE
    ? [EQUIPMENT_CONSTANTS.TYPES.semoir, EQUIPMENT_CONSTANTS.TYPES.semoir_couverts]
    : [EQUIPMENT_CONSTANTS.TYPES.pulve, EQUIPMENT_CONSTANTS.TYPES.traitement, EQUIPMENT_CONSTANTS.TYPES.weeding];
}

/** @param {import('../slices/tasks').Task} [task] */
export function isPhytoTask(task) {
  return (
    task?.type === PHYTO_TASK_TYPE.PHYTO ||
    task?.type === PHYTO_TASK_TYPE.SEMENCE ||
    (!!task?.taskType && isPhytoTaskType(task.taskType))
  );
}

/**
 * @param {string} group
 *
 * @returns {group is import('../constants/taskConstants').VisibleTaskMetadataGroups}
 */
export function isPhytoTaskMetadataGroup(group) {
  return VISIBLE_TASK_METADATA_GROUPS.includes(group);
}

export function getMetadataInputType(metadata) {
  switch (metadata.response_format) {
    case TASK_METADATA_QUESTION_FORMAT.OPEN:
      return metadata.response_type === TASK_METADATA_QUESTION_TYPE.DATE ? 'date' : 'text';
    case TASK_METADATA_QUESTION_FORMAT.YES_OR_NO:
    case TASK_METADATA_QUESTION_FORMAT.SINGLE_CLOSED:
      return 'radio';
    case TASK_METADATA_QUESTION_FORMAT.MULTI_CLOSED:
      return 'checkbox';
    case TASK_METADATA_QUESTION_FORMAT.ADMIN:
    case TASK_METADATA_QUESTION_FORMAT.USER:
      return 'list';
    default:
      return 'text';
  }
}

export function getMetadataInputProps(metadata, responses, onResponsesChange, listItems, validate) {
  const type = getMetadataInputType(metadata);

  switch (type) {
    case 'radio':
      return {
        items:
          metadata.response_format === TASK_METADATA_QUESTION_FORMAT.YES_OR_NO
            ? [
                {
                  label: getMetadataResponseLabel('true', metadata.response_format),
                  checked: responses.includes('true'),
                },
                {
                  label: getMetadataResponseLabel('false', metadata.response_format),
                  checked: responses.includes('false'),
                },
              ]
            : (metadata.defined_responses || []).map((response) => ({
                label: response,
                checked: responses.includes(response),
              })),
        checkItem: (index) =>
          index == null
            ? onResponsesChange([''])
            : metadata.response_format === TASK_METADATA_QUESTION_FORMAT.YES_OR_NO
              ? onResponsesChange([index === 0 ? 'true' : 'false'])
              : onResponsesChange([(metadata.defined_responses || [])[index]]),
        error: validate?.(),
      };
    case 'checkbox':
      return {
        items: metadata.defined_responses,
        onChange: (label) => {
          const newResponses = responses.includes(label)
            ? responses.filter((r) => r !== label)
            : [...responses.filter((r) => r !== ''), label];
          if (newResponses.length === 0) {
            return onResponsesChange(['']);
          }
          return onResponsesChange(newResponses);
        },
        selection: responses,
        error: validate?.(),
      };
    case 'date':
      return {
        date: responses[0] ? moment(responses[0], 'DD/MM/YYYY') : null,
        onDateChange: (date) => onResponsesChange(date ? [date.format('DD/MM/YYYY')] : []),
        withResetButton: true,
      };
    case 'list':
      return {
        title: I18n.t('Metadata.customer_selector_label'),
        list: listItems,
        getItemLabel: getUserName,
        selection: [listItems.find((user) => getUserName(user) === responses[0])].filter(Boolean),
        setSelection: (users) =>
          onResponsesChange(responses[0] === getUserName(users[0]) ? [] : [getUserName(users[0])]),
        getSelectionLabel: (s) => (s.length === 1 ? getUserName(s[0]) : ''),
        withResetButton: true,
      };
    default: {
      const isNumberInput = metadata.response_type === TASK_METADATA_QUESTION_TYPE.NUMBER;
      const value = responses[0] || '';
      const error = validate?.(value) || (isNumberInput ? isNumber(value) : undefined);
      return {
        value,
        onValueChange: ({ target: { value } }) => onResponsesChange([value]),
        validate: (value) => validate?.(value) || (isNumberInput ? isNumber(value) : undefined),
        type: isNumberInput ? 'number' : 'text',
        error,
      };
    }
  }
}

export function getMetadataUserList(metadata, allUsers, clusterId) {
  const usersOfCluster = allUsers
    .filter((user) => !!user.cluster_roles.find((cr) => cr.cluster_id === clusterId))
    .filter(
      metadata.response_format === TASK_METADATA_QUESTION_FORMAT.ADMIN
        ? (user) =>
            user.cluster_roles.find((cr) => cr.cluster_id === clusterId && cr.role === USER_CONSTANTS.ROLE.ADMIN)
        : () => true,
    )
    .sort((a, b) => sortAlphaBeta(getUserName(a), getUserName(b)));

  return usersOfCluster;
}

export function getComputedQuantity(articleId, clientId, task) {
  return (
    ((task?.articles || []).find((a) => a.id === articleId)?.values || []).find((v) => v.client_cluster_id === clientId)
      ?.computed_worked_value || null
  );
}

export function getComputedParcelQuantity(parcelId, articleId, task) {
  if (articleId === PHYTO_VIRTUAL_ARTICLE_ID) {
    return (task?.parcels || []).find((p) => p.parcel_id === parcelId)?.computed_worked_area || null;
  }
  return (
    ((task?.parcels || []).find((p) => p.parcel_id === parcelId)?.details || []).find((d) => d.article_id === articleId)
      ?.computed_worked_value || null
  );
}

/** Combines parcels from selected worksites into a single list of parcels with summed data */
export function getComputedParcelsFromSelectedWorksites(selectedParcelsByWorksite) {
  return Object.values(selectedParcelsByWorksite).reduce((acc, parcels) => {
    parcels.forEach((parcel) => {
      const existingParcel = acc.find((p) => p.id === parcel.id);
      if (!existingParcel) {
        acc.push({ ...parcel });
      } else {
        existingParcel.from_date = moment.min(moment(existingParcel.from_date), moment(parcel.from_date)).format();
        existingParcel.to_date = moment.max(moment(existingParcel.to_date), moment(parcel.to_date)).format();
      }
    });
    return acc;
  }, []);
}

export function getTaskStatusLabel(status, options = {}) {
  return I18n.t(`Tasks.status_${status}${options.short && status === TASK_STATUS.DRAFT ? '_short' : ''}`);
}

export function getTaskStatusColor(status) {
  switch (status) {
    case TASK_STATUS.TO_DO:
      return colors('cyan');
    case TASK_STATUS.IN_PROGRESS:
      return colors('orange');
    case TASK_STATUS.DRAFT:
      return colors('yellow');
    case TASK_STATUS.BILLED:
      return colors('green');
    case TASK_STATUS.ARCHIVED:
      return colors('grey');
    default:
      return colors('bleen');
  }
}

export function getAvailableTaskStatuses(currentStatus) {
  switch (currentStatus) {
    case TASK_STATUS.TO_DO:
      return [TASK_STATUS.TO_DO, TASK_STATUS.IN_PROGRESS, TASK_STATUS.DRAFT, TASK_STATUS.DONE, TASK_STATUS.ARCHIVED];
    case TASK_STATUS.IN_PROGRESS:
      return [TASK_STATUS.IN_PROGRESS, TASK_STATUS.DRAFT, TASK_STATUS.DONE, TASK_STATUS.ARCHIVED];
    case TASK_STATUS.DRAFT:
      return [TASK_STATUS.DRAFT, TASK_STATUS.DONE, TASK_STATUS.BILLED, TASK_STATUS.ARCHIVED];
    case TASK_STATUS.DONE:
      return [TASK_STATUS.DONE, TASK_STATUS.BILLED, TASK_STATUS.ARCHIVED];
    case TASK_STATUS.BILLED:
      return [TASK_STATUS.DONE, TASK_STATUS.BILLED, TASK_STATUS.ARCHIVED];
    case TASK_STATUS.ARCHIVED:
      return [TASK_STATUS.DONE, TASK_STATUS.BILLED, TASK_STATUS.ARCHIVED];
    default:
      return [];
  }
}

export function getTaskStatusActions(currentStatus) {
  switch (currentStatus) {
    case TASK_STATUS.TO_DO:
      return [TASK_STATUS.IN_PROGRESS, TASK_STATUS.DRAFT, TASK_STATUS.DONE];
    case TASK_STATUS.IN_PROGRESS:
      return [TASK_STATUS.TO_DO, TASK_STATUS.DRAFT, TASK_STATUS.DONE];
    case TASK_STATUS.DRAFT:
      return [TASK_STATUS.TO_DO, TASK_STATUS.IN_PROGRESS, TASK_STATUS.DONE];
    case TASK_STATUS.DONE:
      return [TASK_STATUS.TO_DO, TASK_STATUS.IN_PROGRESS, TASK_STATUS.DRAFT];
    case TASK_STATUS.ARCHIVED:
      return [TASK_STATUS.TO_DO, TASK_STATUS.IN_PROGRESS, TASK_STATUS.DRAFT, TASK_STATUS.DONE];
    default:
      return [];
  }
}

export function validateTaskForStatus(task, taskType, status) {
  const metadataErrors = (taskType?.metadata || []).map((metadata) => [
    metadata.id,
    metadata.mandatory
      ? !((task.metadata || []).find((m) => m.id === metadata.id)?.responses || []).find(
          (response) => response.trim() !== '',
        )
      : false,
  ]);
  const isPhytoTask = isPhytoTaskType(taskType);
  const parcelErrors = isPhytoTask
    ? (task.parcels || []).map((p) => [p.parcel_id, status === TASK_STATUS.TO_DO ? !p.goal_area : !p.worked_area])
    : [];
  const clientError = !(task.client_cluster_ids || []).length && !task.client_cluster_id;
  const allErrors = {
    clientId: clientError,
    articles: !isPhytoTask && !task.articles?.length,
    articleValues:
      !isPhytoTask && !clientError
        ? // at least one article with all of its values filled
          task.articles?.every((a) =>
            a.values
              ? a.values.some((v) =>
                  status === TASK_STATUS.TO_DO
                    ? v.goal_value == null
                    : (v.range_value || []).length === 0 && v.worked_value == null,
                )
              : status === TASK_STATUS.TO_DO
                ? a.goal_value == null
                : a.worked_value == null,
          )
        : false,
    fromDate: !task.from_date,
    toDate: !task.to_date,
    fromDateBeforeToDate: task.from_date && task.to_date && moment(task.from_date).isSameOrAfter(moment(task.to_date)),
    // all mandatory metadata are filled
    metadata: taskType && !!metadataErrors.find(([, error]) => error) ? Object.fromEntries(metadataErrors) : false,
    mixture: isPhytoTask && taskType.label === PHYTO_TASK_TYPE.PHYTO && !task.product_usages?.length,
    // every client has at least one parcel
    parcels:
      isPhytoTask &&
      (task.client_cluster_ids || [task.client_cluster_id])
        .filter(Boolean)
        .some((clientId) => !(task.parcels || []).some((p) => p.client_cluster_id === clientId)),
    // each parcel has its phyto area filled
    eachParcel: parcelErrors.find(([, error]) => error) ? Object.fromEntries(parcelErrors) : false,
    // each parcel has a client selected
    parcelClients: !!(task.parcels || []).find((p) => !p.client_cluster_id),
  };

  switch (status) {
    case TASK_STATUS.ARCHIVED:
      return pick(allErrors, ['clientId', 'articles', 'fromDateBeforeToDate', 'parcelClients']);
    case TASK_STATUS.TO_DO:
      return pick(allErrors, ['clientId', 'articles', 'fromDateBeforeToDate', 'mixture', 'parcelClients']);
    case TASK_STATUS.IN_PROGRESS:
      return pick(allErrors, ['clientId', 'articles', 'fromDate', 'fromDateBeforeToDate', 'mixture', 'parcelClients']);
    case TASK_STATUS.DRAFT:
      return pick(allErrors, [
        'clientId',
        'articles',
        'fromDate',
        'toDate',
        'fromDateBeforeToDate',
        'metadata',
        'mixture',
        'parcelClients',
        'parcels',
      ]);
    default:
      return allErrors;
  }
}

export function checkShouldAskForParcelCreation(task, toStatus) {
  // should ask for parcel creation when going from DRAFT to DONE and there are anon parcels in the task
  if (task.status === TASK_STATUS.DRAFT && toStatus === TASK_STATUS.DONE) {
    return task.parcels?.length && !!task.parcels.find((p) => !p.cluster_id);
  }
  return false;
}

export function getWorksiteParcelsWithNoTasks(worksite) {
  const parcelsWithNoTasks = (worksite.worked_parcel_with_tasks || []).filter((p) => !p.task_ids?.length);
  if (parcelsWithNoTasks.length === 0) {
    // all parcels already have tasks, so we show all of them
    return worksite.worked_parcel_with_tasks || [];
  }
  return parcelsWithNoTasks;
}

export function getPDFColorFromColorChoice(colorChoice) {
  switch (colorChoice) {
    case COLOR_PARCELS_BY.CLUSTER:
    case COLOR_TRACKS_BY.CLUSTER:
      return 'cluster';
    case COLOR_PARCELS_BY.CROP:
      return 'crop';
    case COLOR_PARCELS_BY.TAG:
      return 'tag';
    case COLOR_TRACKS_BY.EQUIPMENT:
      return 'equipment';
    default:
      return '';
  }
}

export function getColorChoiceFromPDFColor(pdfColor) {
  switch (pdfColor) {
    case 'cluster':
      return COLOR_PARCELS_BY.CLUSTER;
    case 'crop':
      return COLOR_PARCELS_BY.CROP;
    case 'tag':
      return COLOR_PARCELS_BY.TAG;
    case 'equipment':
      return COLOR_TRACKS_BY.EQUIPMENT;
    default:
      return COLOR_PARCELS_BY.DEFAULT;
  }
}

/** @typedef {import('../slices/tasks').TaskMetadata} TaskMetadata */
/** @typedef {import('../constants/taskConstants').TaskStatus} TaskStatus */
/**
 * @param {TaskMetadata[]} metadata Array of metadata questions
 * @param {TaskStatus} [taskStatus] Task status, which defines the default responses
 * @param {boolean} [changeDefaultResponses] Whether to change the default responses in the returned value
 *
 * @returns {Record<string, TaskMetadata[]>} Object with metadata questions grouped by the
 * group_by property
 */
export function formatTaskMetadataByGroup(metadata, taskStatus, changeDefaultResponses = false) {
  return groupBy(
    metadata
      .filter((m) => VISIBLE_TASK_METADATA_GROUPS.includes(m.group_by))
      .map((m) => {
        if (
          taskStatus &&
          (!m.default_responses_apply_with_task_status ||
            m.default_responses_apply_with_task_status.includes(taskStatus)) &&
          changeDefaultResponses &&
          !m.responses &&
          m.default_responses
        ) {
          return { ...m, responses: m.default_responses };
        } else if (
          m.default_responses_apply_with_task_status &&
          !m.default_responses_apply_with_task_status.includes(taskStatus) &&
          changeDefaultResponses
        ) {
          return { ...m, responses: undefined };
        } else {
          return m;
        }
      }),
    'group_by',
  );
}

export function getTaskArticlesForParcels(
  { articles: taskArticles, owner_cluster_id, status },
  userUnits,
  isPhytoTask,
) {
  let articles = taskArticles || [];

  if (isPhytoTask && !articles.find((a) => a.id === PHYTO_VIRTUAL_ARTICLE_ID)) {
    articles = articles.concat([
      getVirtualPhytoArticle({ clusterId: owner_cluster_id, userUnits, taskStatus: status }),
    ]);
  }

  return articles.toSorted(sortTaskArticles);
}

/**
 * @template {import('../slices/tasks').AnyTask} T
 * @param {T | null} task
 *
 * @returns {T | null}
 */
export function formatTaskWithCompleteInfo(
  task,
  equipmentsById,
  equipmentModelsById,
  mixturesByClusterId,
  parcelsById,
  cropsById,
  taskTypesByClusterId,
  phytoTaskTypes,
  units,
) {
  if (!task) return null;

  const fromDate = task.from_date ? moment(task.from_date) : undefined;
  const toDate = task.to_date ? moment(task.to_date) : undefined;
  const createdAt = moment(task.created_at);
  const equipments = (task.equipment_instance_ids || []).map((id) => equipmentsById[id]).filter(Boolean);
  const parcels = task?.parcels?.map((p) => ({ ...p, ...parcelsById[p.parcel_id], parcel_task_id: p.id })) || [];
  const crop = cropsById[task.crop_range_id];
  const taskType =
    taskTypesByClusterId[task.owner_cluster_id]?.find((t) => t.id === task.task_type_id) ||
    phytoTaskTypes.find((t) => t.id === task.task_type_id);

  return {
    ...task,
    equipment_instance_id: undefined,
    assigned_to_id: undefined,
    assigned_to_email: undefined,
    assigned_to_fullname: undefined,
    assigned_users: task.assigned_users || [],
    equipments,
    equipment_model: equipmentModelsById[task.equipment_model_id],
    fromDate,
    toDate,
    createdAt,
    sortDate: task.from_date || task.created_at,
    area: (task.parcels || []).reduce((p, c) => p + (c.worked_area || c.goal_area || 0), 0),
    metadata: task.metadata || [],
    articles: (task.articles || []).map((a) =>
      a.id === PHYTO_VIRTUAL_ARTICLE_ID
        ? {
            ...getVirtualPhytoArticle({ clusterId: task.owner_cluster_id, taskStatus: task.status, userUnits: units }),
            userUnit: units,
            values: a.values?.map((v) => ({
              ...v,
              worked_value:
                v.worked_value == undefined ? undefined : convertAreaToSquaredMeters(v.worked_value, units.area),
              goal_value: v.goal_value == undefined ? undefined : convertAreaToSquaredMeters(v.goal_value, units.area),
            })),
          }
        : a,
    ),
    observations: task.observations?.map((o) => {
      if (!o.task_id) {
        return o;
      }
      const clientTask = task.tasks?.find((t) => t.id === o.task_id);
      if (!clientTask) {
        return o;
      }
      return { ...o, client_cluster_id: clientTask.client_cluster_id };
    }),
    parcels,
    crop,
    taskType,
  };
}

export function getNewTaskStateWithUpdate(state, update, parcelsById) {
  const { fromDate, toDate, clusters, ownerClusterId, equipments } = update;

  const newSelectedWorksites = state.selectedWorksites.filter((w) => {
    // check worksite dates
    const startDateMatches =
      'fromDate' in update ? (fromDate ? moment(w.from_date).isSameOrAfter(moment(fromDate), 'day') : false) : true;
    const endDateMatches =
      'toDate' in update ? (toDate ? moment(w.to_date).isSameOrBefore(moment(toDate), 'day') : false) : true;

    // check worksite cluster
    const worksiteClusterId = w.cluster_id;
    const isWorksiteClusterOwnerCluster = 'ownerClusterId' in update ? ownerClusterId === worksiteClusterId : true;
    const isWorksiteClusterInNewSelection =
      'clusters' in update ? clusters.some((c) => c.id === worksiteClusterId) : true;
    const clusterMatches = isWorksiteClusterOwnerCluster || isWorksiteClusterInNewSelection;

    // check equipments
    const worksiteEquipment = getEquipmentFromWorksite(w);
    const equipmentMatches = 'equipments' in update ? equipments.some((e) => e.id === worksiteEquipment?.id) : true;

    return startDateMatches && endDateMatches && clusterMatches && equipmentMatches;
  });

  // filter worksite parcels based on new worksites
  const newSelectedParcels = Object.fromEntries(
    Object.entries(state.selectedParcels)
      .filter(([worksiteKey]) => {
        const worksite = newSelectedWorksites.find((w) => getWorksiteKey(w) === worksiteKey);
        if (!worksite) return false;
        return true;
      })
      .map(([worksiteKey, parcels]) => {
        const filteredParcels =
          'clusters' in update && 'ownerClusterId' in update && parcelsById
            ? parcels.filter((p) =>
                filterWorksiteParcelsForTask(
                  p.id,
                  ownerClusterId,
                  clusters.map((c) => c.id),
                  parcelsById,
                ),
              )
            : parcels;
        return [worksiteKey, filteredParcels];
      }),
  );

  // filter manual parcel selection based on new clusters selection
  const newManualParcelSelection = state.manualParcelSelection.filter((parcel) => {
    const clusterId = parcel.current_tenant_cluster_id || parcel.cluster_id;
    const isClusterInNewSelection = 'clusters' in update ? clusters.some((c) => c.id === clusterId) : true;
    return isClusterInNewSelection;
  });

  // filter article quantities based on new parcels selection
  const newArticleQuantitiesByParcelId = Object.fromEntries(
    Object.entries(state.articleQuantitiesByParcelId).filter(([parcelId]) => {
      const isSelected =
        Object.values(newSelectedParcels).some((parcels) => parcels.some((p) => p.id === Number(parcelId))) ||
        Object.values(newManualParcelSelection).some((p) => p.id === Number(parcelId));
      return isSelected;
    }),
  );

  return {
    selectedWorksites: newSelectedWorksites,
    selectedParcels: newSelectedParcels,
    manualParcelSelection: newManualParcelSelection,
    articleQuantitiesByParcelId: newArticleQuantitiesByParcelId,
  };
}

function canChangeStatusTo(task, taskType, status) {
  const shouldAskForParcelCreation = checkShouldAskForParcelCreation(task, status);
  return (
    !shouldAskForParcelCreation &&
    !Object.values(validateTaskForStatus(task, taskType, status)).reduce((acc, isError) => acc || isError, false)
  );
}

/**
 * @param {import('../slices/tasks').AnyTask[]} selectedTasks
 * @param {import('../slices/taskTypes').TaskType[]} allTaskTypes
 *
 * @returns {import('../constants/taskConstants').TaskStatus[]}
 */
export function getNewAvailableStatusesForTasks(selectedTasks, allTaskTypes) {
  return (
    [
      ...new Set(
        selectedTasks.map((t) => {
          const taskType = allTaskTypes.find((tt) => tt.id === t.task_type_id);
          return getAvailableTaskStatuses(t.status).filter((s) => canChangeStatusTo(t, taskType, s));
        }),
      ),
    ].reduce((p, c) => {
      if (!p) {
        return c;
      }
      return p.filter((s) => c.includes(s));
    }, null) || []
  );
}

export function formatTaskDates(fromDate, toDate, format = 'L', withTime = false) {
  const formatDateTime = (date) => (withTime ? date.format(`${format} LT`) : date.format(format));

  if (!fromDate && !toDate) {
    return '-';
  }
  if (!fromDate || !toDate) {
    return formatDateTime(fromDate || toDate);
  }
  if (withTime) {
    return fromDate.isSame(toDate, 'day')
      ? `${fromDate.format(format)}, ${fromDate.format('LT')} - ${toDate.format('LT')}`
      : `${formatDateTime(fromDate)} - ${formatDateTime(toDate)}`;
  }

  // a range ending exactly at midnight stops at the end of the previous day, the last one it really covers
  const lastDay = toDate.isSame(moment(toDate).startOf('day')) ? moment(toDate).subtract(1, 'day') : toDate;

  return lastDay.isSameOrBefore(fromDate, 'day')
    ? fromDate.format(format)
    : `${fromDate.format(format)} - ${lastDay.format(format)}`;
}

export function getTotalArticleValue(article, status) {
  let value;
  if (status === TASK_STATUS.TO_DO) {
    value = (article?.values || []).reduce((acc, value) => acc + (value.goal_value || 0), 0);
  } else {
    if (article.is_range) {
      const formattedArticle = {
        ...article,
        ranges: Object.fromEntries(
          (article?.values || []).map((v) => [
            v.client_cluster_id,
            v.range_value?.map((r) => ({ ...r, from: r.lower, to: r.upper })),
          ]),
        ),
      };
      value = (article?.values || [])
        .map((v) => getArticleRangeValue(formattedArticle, v.client_cluster_id) ?? v.worked_value)
        .reduce((acc, value) => acc + (value || 0), 0);
    } else {
      value = (article?.values || []).reduce((acc, value) => acc + (value.worked_value || 0), 0);
    }
  }

  return value;
}

/**
 * @param {import('../slices/tasks').Task} task
 *
 * @returns {import('../slices/tasks').Task}
 */
export function transformTaskToManyClients(task) {
  return {
    ...task,
    from_date: task.from_ts,
    to_date: task.to_ts,
    client_cluster_ids: [task.client_cluster_id].filter(Boolean),
    articles: (task.articles || []).map((article) => ({
      ...article,
      values: [
        {
          client_cluster_id: task.client_cluster_id,
          goal_value: article.goal_value ?? (task.status === TASK_STATUS.TO_DO ? article.value : null),
          worked_value: article.worked_value ?? (task.status === TASK_STATUS.TO_DO ? null : article.value),
          range_value: article.range_value,
          computed_worked_value: article.computed_worked_value,
        },
      ],
    })),
  };
}

export function sortTaskDate(a, b) {
  if (a.fromDate && b.fromDate) return a.fromDate.unix() - b.fromDate.unix();
  if (a.fromDate) return -1;
  if (b.fromDate) return 1;
  return moment(a.created_at).unix() - moment(b.created_at).unix();
}
